import type { ProviderType } from "./provider-types";
import type { BillingAccess } from "./billing.types";

export type ProviderAccountRole = ProviderType;
export type RegistrationAccountRole = ProviderAccountRole | "kunde";
export type AccountRole = RegistrationAccountRole | "admin";

export type ProviderStatus = "pending" | "approved" | "rejected" | "suspended";

export type MembershipStatus = "free" | "active" | "inactive";

export type ProviderProfile = {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  phone: string | null;
  role: AccountRole;
  providerStatus: ProviderStatus;
  membershipStatus: MembershipStatus;
};

// Kept as a type alias for internal/external imports compiled against the
// previous name. New code should use ProviderProfile.
export type ContractorProfile = ProviderProfile;

export type AuthenticatedAccount = {
  authenticated: true;
  configured: true;
  user: {
    id: string;
    email: string;
    emailConfirmed: boolean;
  };
  profile: ProviderProfile | null;
  billing: BillingAccess;
  canExpressInterest: boolean;
  /** Contact access is granted per job. This legacy global flag is always false. */
  canViewContacts: boolean;
};

export type AnonymousAccount = {
  authenticated: false;
  configured: boolean;
};

export type AuthStatus = AuthenticatedAccount | AnonymousAccount;

export function isSafeInternalPath(value: string | undefined): value is string {
  const hasUnsafeCharacter = Array.from(value ?? "").some((character) => {
    const code = character.charCodeAt(0);
    return character === "\\" || code < 0x20 || code === 0x7f;
  });
  return Boolean(
    value &&
    value.length <= 300 &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !hasUnsafeCharacter,
  );
}

export function hasContractorAccess(profile: ProviderProfile | null): boolean {
  return Boolean(
    profile &&
    profile.role === "auftragnehmer" &&
    profile.providerStatus === "approved" &&
    profile.membershipStatus === "active",
  );
}
